import { Field, Formik } from 'formik';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroText,
} from '../../shared/components';
import { CompanyNda, CurrentUser } from '../../shared/models';
import { FormikUtils } from '../../shared/utils';
import { useMutation } from 'react-query';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { useApi } from '../../shared/context';
import { useSnackbar } from 'notistack';
import { useNavigateToNextPage } from '../../shared/hooks';
import { Box, Grid, Link } from '@mui/material';
import { NdaText } from './NdaText';
import { getApiResponseData } from '../../shared/utils/axios';

interface SignNdaDrawerProps {
	open: boolean;
	dealGuid: string | undefined;
	companyGuid: string | undefined;
	currentUser: CurrentUser;
	onClose: () => void;
}

export const SignNdaDrawer = ({ open, dealGuid, companyGuid, currentUser, onClose }: SignNdaDrawerProps) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const navigateToNextPage = useNavigateToNextPage();

	const ndaMutation = useMutation(
		async ({ values }: IntroductionMutationVariables) => {
			const { data, error } = await getApiResponseData<CompanyNda>(
				api.post(CerebroApiEndpoints.Deals.Nda(dealGuid), values)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
				onClose();
				navigateToNextPage();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={open} onClose={onClose}>
			<Formik
				enableReinitialize
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(ndaMutation.mutate)}
				initialValues={{
					name: currentUser ? currentUser.firstName + ' ' + currentUser.lastName : '',
					title: '',
				}}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => (
					<CerebroForm onSubmit={handleSubmit}>
						<CerebroHeading size="h2" component="h3" gutterBottom>
							Mutually Exclusive NDA
						</CerebroHeading>
						<CerebroText variant="small">
							Please review the NDA below and enter your name and title to electronically record your
							acceptance.
						</CerebroText>
						<Box sx={{ marginY: 3 }}>
							<NdaText />

							<CerebroText variant="small" sx={{ marginBottom: 3 }}>
								<Link
									underline="hover"
									target="_blank"
									rel="noreferrer"
									href={CerebroApiEndpoints.CompanyNdas.AbsoluteBlank}
								>
									Click here to download a copy of this document.
								</Link>
							</CerebroText>
						</Box>

						<hr />
						<Grid container spacing={3} sx={{ marginTop: 0 }}>
							<Grid item>
								<CerebroLabel
									required
									htmlFor="name"
									error={FormikUtils.IsInvalid('name', errors, touched)}
								>
									Name
								</CerebroLabel>
								<Field
									as={CerebroInput}
									id="name"
									name="name"
									disabled={isSubmitting}
									value={values.name}
									error={FormikUtils.IsInvalid('name', errors, touched)}
								/>
								{FormikUtils.IsInvalid('name', errors, touched) && (
									<CerebroText variant="error">{errors.name}</CerebroText>
								)}
							</Grid>

							<Grid item>
								<CerebroLabel
									required
									htmlFor="title"
									error={FormikUtils.IsInvalid('title', errors, touched)}
								>
									Title
								</CerebroLabel>
								<Field
									as={CerebroInput}
									id="title"
									name="title"
									disabled={isSubmitting}
									value={values.title}
									error={FormikUtils.IsInvalid('title', errors, touched)}
								/>
								{FormikUtils.IsInvalid('title', errors, touched) && (
									<CerebroText variant="error">{errors.title}</CerebroText>
								)}
							</Grid>
						</Grid>

						<Grid container sx={{ marginY: 3 }}>
							<Grid item>
								<CerebroPrimaryButton loading={isSubmitting} type="submit">
									Sign &amp; Execute NDA
								</CerebroPrimaryButton>
							</Grid>
						</Grid>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

function validateForm(values: IntroductionValues) {
	const errors: IntroductionValueErrors = {};

	if (!values.name) {
		errors.name = 'Your full name is required!';
	}

	if (!values.title) {
		errors.title = 'Your title is required!';
	}

	return errors;
}

type IntroductionMutationVariables = FormikUtils.MutateOnSubmitVariables<IntroductionValues>;
interface IntroductionValues {
	name: string;
	title: string;
}

interface IntroductionValueErrors {
	name?: string;
	title?: string;
}
